import type React from "react"
import { useState } from "react"
import { motion, AnimatePresence, easeInOut } from "framer-motion"
import { Nav, Container, Row, Col } from "react-bootstrap"
import { FaTicketAlt, FaGift, FaCog, FaStar, FaCoins } from "react-icons/fa"
import GestionCodesPromo from "../codePromo/GestionCodesPromo"
import "../../../css/PromoManagement.css"
import GestionCommissionsUser from "./GestionCommissionsUser"
import GestionPoints from "../points-management/GestionPoints"
import AdminAssetTable from "../avoir/AdminAssetTable"
import "../../../css/PointsManagement.css"

type TabType = "promo-codes" | "commissions" | "points" | "avoirs"

const PromoManagementLayout: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("promo-codes")

  const tabVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3, ease: easeInOut }
    },
    exit: {
      opacity: 0,
      x: -20,
      transition: {
        duration: 0.2,
      },
    },
  }

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: easeInOut }
    },
  }

  return (
    <Container fluid className="promo-management-container">
      <motion.div 
        variants={headerVariants}
        initial="hidden"
        animate="visible"
        className="promo-management-header">
        <Row className="align-items-center mb-4">
          <Col>
            <div className="d-flex align-items-center">
              <FaCog className="header-icon me-3" />
              <h1 className="page-title-header mb-0">Gestion des Promotions</h1>
            </div>
            <p className="page-subtitle mt-2">Gérez les codes promotionnels, avoirs, points et commissions de fidélité des utilisateurs</p>
          </Col>
        </Row>
      </motion.div>

      <Row>
        <Col lg={2} className="sidebar-col">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="promo-nav-container"
          >
            <Nav variant="pills" className="flex-column promo-nav">
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "promo-codes"}
                  onClick={() => setActiveTab("promo-codes")}
                  className="promo-nav-link"
                >
                  <FaTicketAlt className="nav-icon" />
                  <span>Codes Promo</span>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "points"}
                  onClick={() => setActiveTab("points")}
                  className="promo-nav-link"
                >
                  <FaStar className="nav-icon" />
                  <span>Gestion des Points</span>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "avoirs"}
                  onClick={() => setActiveTab("avoirs")}
                  className="promo-nav-link"
                >
                  <FaCoins className="nav-icon" />
                  <span>Gestion des Avoirs</span>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "commissions"}
                  onClick={() => setActiveTab("commissions")}
                  className="promo-nav-link"
                >
                  <FaGift className="nav-icon" />
                  <span>Commissions de fidélité</span>
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </motion.div>
        </Col>

        <Col lg={10} className="content-col">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="tab-content-container"
            >
              {activeTab === "promo-codes" && <GestionCodesPromo />}
              {activeTab === "commissions" && <GestionCommissionsUser />}
              {activeTab === "points" && <GestionPoints />}
              {activeTab === "avoirs" && <AdminAssetTable />}
            </motion.div>
          </AnimatePresence>
        </Col>
      </Row>
    </Container>
  )
}

export default PromoManagementLayout